import {
  Building2,
  CalendarDays,
  CheckSquare,
  FileText,
  Flag,
  FolderKanban,
  HardDrive,
  Lightbulb,
  Package,
  Receipt,
  Scale,
  Users,
  Wallet,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { PlatformCounts } from "../types";

type CountCard = {
  key: keyof PlatformCounts;
  label: string;
  icon: LucideIcon;
  color: string;
  href: string;
};

const ROW_BUSINESS: CountCard[] = [
  {
    key: "companies",
    label: "Companies",
    icon: Building2,
    color: "hsl(210,100%,40%)",
    href: "/companies",
  },
  {
    key: "products",
    label: "Products",
    icon: Package,
    color: "hsl(262,83%,45%)",
    href: "/products",
  },
  {
    key: "projects",
    label: "Projects",
    icon: FolderKanban,
    color: "hsl(180,72%,30%)",
    href: "/projects",
  },
  {
    key: "contacts",
    label: "Contacts",
    icon: Users,
    color: "hsl(210,100%,40%)",
    href: "/contacts",
  },
];

const ROW_DAILY: CountCard[] = [
  {
    key: "meetings",
    label: "Meetings",
    icon: CalendarDays,
    color: "hsl(262,83%,45%)",
    href: "/meetings",
  },
  {
    key: "tasks",
    label: "Tasks",
    icon: CheckSquare,
    color: "hsl(210,100%,40%)",
    href: "/tasks",
  },
  {
    key: "actions",
    label: "Actions",
    icon: Zap,
    color: "hsl(35,90%,35%)",
    href: "/actions",
  },
  {
    key: "notes",
    label: "Notes",
    icon: FileText,
    color: "hsl(215,25%,40%)",
    href: "/notes",
  },
  {
    key: "milestones",
    label: "Milestones",
    icon: Flag,
    color: "hsl(142,72%,28%)",
    href: "/milestones",
  },
];

const ROW_PLANNING_FINANCE: CountCard[] = [
  {
    key: "ideas",
    label: "Ideas",
    icon: Lightbulb,
    color: "hsl(38,92%,50%)",
    href: "/ideas",
  },
  {
    key: "decisions",
    label: "Decisions",
    icon: Scale,
    color: "hsl(262,83%,45%)",
    href: "/decisions",
  },
  {
    key: "budgets",
    label: "Budgets",
    icon: Wallet,
    color: "hsl(142,72%,28%)",
    href: "/budgets",
  },
  {
    key: "expenses",
    label: "Expenses",
    icon: Receipt,
    color: "hsl(35,90%,35%)",
    href: "/expenses",
  },
];

const ROW_RESOURCES: CountCard[] = [
  {
    key: "mediaFiles",
    label: "Media Files",
    icon: HardDrive,
    color: "hsl(210,100%,40%)",
    href: "/media-vault",
  },
];

type Props = {
  counts: PlatformCounts;
};

function Row({ cards, counts }: { cards: CountCard[]; counts: PlatformCounts }) {
  return (
    <div
      className="grid gap-3"
      style={{
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      }}
    >
      {cards.map((c) => {
        const n = (counts[c.key] as number | undefined) ?? 0;
        return (
          <Link
            key={c.key}
            to={c.href}
            className="rounded-xl border bg-white p-3 transition-all hover:shadow-sm hover:-translate-y-px"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <div className="flex items-center gap-2">
              <c.icon
                className="h-5 w-5 shrink-0"
                style={{ color: c.color }}
                strokeWidth={1.5}
              />
              <span
                className="text-[24px] font-bold leading-none text-foreground"
              >
                {n}
              </span>
            </div>
            <div className="mt-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              {c.label}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export function PlatformCountsWidget({ counts }: Props) {
  return (
    <div className="space-y-3">
      <Row cards={ROW_BUSINESS} counts={counts} />
      <Row cards={ROW_DAILY} counts={counts} />
      <Row cards={ROW_PLANNING_FINANCE} counts={counts} />
      <Row cards={ROW_RESOURCES} counts={counts} />
    </div>
  );
}
